import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  User, Phone, Mail, Calendar, Gift, Copy, Share2, 
  ExternalLink, LogOut, RefreshCw, TrendingUp, Award,
  MessageCircle, Clock, CheckCircle, AlertCircle, Star,
  Play, BookOpen, KeyRound
} from 'lucide-react';
import WorkshopRecordings from './WorkshopRecordings';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const SubscriberDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPwForm, setShowPwForm] = useState(false);
  const [pwNudgeDismissed, setPwNudgeDismissed] = useState(false);
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwDone, setPwDone] = useState(false);

  const handleSetPasswordFromDashboard = async (e) => {
    e.preventDefault();
    setPwMsg('');
    if (pwNew.length < 6) { setPwMsg('Password must be at least 6 characters.'); return; }
    if (pwNew !== pwConfirm) { setPwMsg('Passwords do not match.'); return; }
    setPwSaving(true);
    try {
      const token = localStorage.getItem('subscriber_token');
      const res = await fetch(`${API_URL}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ password: pwNew }),
      });
      const data = await res.json();
      if (res.ok) { setPwDone(true); setShowPwForm(false); }
      else setPwMsg(data.detail || 'Failed to set password');
    } catch (_e) {
      setPwMsg('Network error. Please try again.');
    } finally {
      setPwSaving(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('subscriber_token');
    
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/subscriber/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('subscriber_token');
        navigate('/login');
        return;
      }

      const data = await response.json();
      
      if (response.ok) {
        setDashboardData(data);
      } else {
        setError(data.detail || 'Failed to load dashboard');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    const token = localStorage.getItem('subscriber_token');
    
    // Call logout API
    fetch(`${API_URL}/api/auth/logout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }).catch(() => {});

    // Clear local storage
    localStorage.removeItem('subscriber_token');
    localStorage.removeItem('subscriber_name');
    localStorage.removeItem('subscriber_phone');
    
    navigate('/login');
  };

  const copyReferralCode = () => {
    const code = dashboardData?.referral?.referral_code;
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareReferral = () => {
    const code = dashboardData?.referral?.referral_code;
    if (!code) return;

    const shareText = `Join Upper Circuit Stocks for premium trading education! Use my referral code: ${code}`;
    const shareUrl = `https://uppercircuitstocks.com/?ref=${code}`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Upper Circuit Stocks Referral',
        text: shareText,
        url: shareUrl
      });
    } else {
      navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getSubscriptionStatus = (sub) => {
    const endDate = new Date(sub.end_date);
    const now = new Date();
    
    if (endDate < now) {
      return { status: 'expired', color: 'bg-red-500' };
    }
    
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 7) {
      return { status: 'expiring', color: 'bg-yellow-500' };
    }
    
    return { status: 'active', color: 'bg-green-500' };
  };

  const packageNames = {
    "options_only": "Options Only",
    "options_only_3month": "Options Only (3 Month)",
    "complete_package": "Options/Futures/Cash Complete",
    "complete_package_3month": "Complete Package (3 Month)",
    "equity_only": "Equity Only",
    "equity_only_3month": "Equity Only (3 Month)",
    "workshop": "Intraday Options Trading Workshop"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-golden flex items-center gap-2">
          <RefreshCw className="w-6 h-6 animate-spin" />
          <span>Loading your dashboard...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center p-4">
        <Card className="bg-gray-900 border-red-500/50 max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-400 mb-4">{error}</p>
            <Button onClick={fetchDashboardData} className="bg-golden hover:bg-golden/90 text-black">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { user, subscriptions, active_subscriptions, expired_subscriptions, has_active_subscription, referral, points_transactions, telegram_links, autopay_subscriptions, has_password } = dashboardData || {};

  // Check if user has any active subscription
  const hasActiveAccess = has_active_subscription || (autopay_subscriptions && autopay_subscriptions.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white">
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-sm border-b border-golden/20 py-4 px-4 sticky top-0 z-10">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_invest-links/artifacts/kydgz8tm_WhatsApp%20Image%202025-08-23%20at%203.47.06%20PM.jpeg" 
              alt="Upper Circuit Stocks Logo" 
              className="h-10 w-auto"
            />
            <span className="text-golden font-semibold hidden sm:inline">Subscriber Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/"
              className="inline-flex items-center gap-1.5 bg-golden/10 border border-golden/40 text-golden hover:bg-golden/20 px-2.5 md:px-3 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors"
              data-testid="dashboard-market-intelligence-link"
            >
              <span className="hidden xs:inline">📊</span>
              <span className="hidden sm:inline">Market Intelligence</span>
              <span className="sm:hidden">Markets</span>
            </a>
            <span className="text-gray-400 text-sm hidden sm:inline">
              Welcome, {user?.name || 'Subscriber'}
            </span>
            <Button 
              onClick={handleLogout}
              variant="outline" 
              className="border-golden/50 text-golden hover:bg-golden/10"
              size="sm"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto p-4 max-w-6xl">
        {/* Set-password nudge (for OTP users without a password) */}
        {has_password === false && !pwNudgeDismissed && !pwDone && (
          <Card className="bg-golden/10 border-golden/40 mb-6" data-testid="set-password-nudge">
            <CardContent className="p-4">
              {!showPwForm ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-golden/20 flex items-center justify-center flex-shrink-0">
                      <KeyRound className="w-5 h-5 text-golden" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-golden">Set a password for faster login</h3>
                      <p className="text-sm text-gray-300">Skip the OTP next time — log in instantly with your phone and password.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => setShowPwForm(true)} className="bg-golden hover:bg-golden/90 text-black font-semibold" data-testid="nudge-set-password-btn">
                      Set Password
                    </Button>
                    <button onClick={() => setPwNudgeDismissed(true)} className="text-gray-400 hover:text-white text-sm px-2" data-testid="nudge-dismiss-btn">
                      Not now
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSetPasswordFromDashboard} className="space-y-3" data-testid="dashboard-set-password-form">
                  <h3 className="font-semibold text-golden flex items-center gap-2"><KeyRound className="w-4 h-4" /> Create your password</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input type="password" placeholder="New password (min 6 chars)" value={pwNew}
                      onChange={(e) => { setPwNew(e.target.value); if (pwMsg) setPwMsg(''); }} required
                      className="bg-gray-800 border-gray-700 text-white placeholder-gray-500" data-testid="dashboard-new-password" />
                    <Input type="password" placeholder="Confirm password" value={pwConfirm}
                      onChange={(e) => { setPwConfirm(e.target.value); if (pwMsg) setPwMsg(''); }} required
                      className="bg-gray-800 border-gray-700 text-white placeholder-gray-500" data-testid="dashboard-confirm-password" />
                  </div>
                  {pwMsg && <p className="text-sm text-red-400" data-testid="dashboard-pw-error">{pwMsg}</p>}
                  <div className="flex items-center gap-2">
                    <Button type="submit" disabled={pwSaving || !pwNew || !pwConfirm} className="bg-golden hover:bg-golden/90 text-black font-semibold" data-testid="dashboard-save-password-btn">
                      {pwSaving ? 'Saving...' : 'Save Password'}
                    </Button>
                    <button type="button" onClick={() => { setShowPwForm(false); setPwMsg(''); }} className="text-gray-400 hover:text-white text-sm px-2">
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        )}
        {pwDone && (
          <Card className="bg-green-900/30 border-green-500/50 mb-6" data-testid="set-password-success">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
              <p className="text-green-300 text-sm">Password set! You can now log in with your phone and password next time.</p>
            </CardContent>
          </Card>
        )}

        {/* Expired Subscription Banner */}
        {expired_subscriptions && expired_subscriptions.length > 0 && !hasActiveAccess && (
          <Card className="bg-red-900/30 border-red-500/50 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-red-400" />
                  <div>
                    <h3 className="font-semibold text-red-300">Your subscription has expired</h3>
                    <p className="text-sm text-red-400">Renew now to regain access to Telegram groups and trading signals</p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/')}
                  className="bg-red-500 hover:bg-red-600 text-white"
                >
                  Renew Now
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* User Info Card */}
        <Card className="bg-gray-900/50 border-golden/30 mb-6">
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-6 items-center">
              <div className="w-16 h-16 rounded-full bg-golden/20 flex items-center justify-center">
                <User className="w-8 h-8 text-golden" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-white">{user?.name || 'Subscriber'}</h2>
                <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-400">
                  {user?.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {user.phone}
                    </span>
                  )}
                  {user?.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {user.email}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Subscriptions & Telegram */}
          <div className="lg:col-span-2 space-y-6">
            {/* Active Subscriptions */}
            <Card className="bg-gray-900/50 border-golden/30">
              <CardHeader>
                <CardTitle className="text-golden flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Your Subscriptions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {subscriptions?.length > 0 ? (
                  <div className="space-y-4">
                    {subscriptions.map((sub, index) => {
                      const { status, color } = getSubscriptionStatus(sub);
                      return (
                        <div 
                          key={sub.id || index}
                          className="p-4 bg-gray-800/50 rounded-lg border border-gray-700"
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h3 className="font-semibold text-white">
                                {packageNames[sub.subscription_type] || sub.subscription_type}
                              </h3>
                              <p className="text-sm text-gray-400">
                                ₹{sub.amount_paid?.toLocaleString() || 0}
                              </p>
                            </div>
                            <Badge className={`${color} text-white capitalize`}>
                              {status}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                            <div>
                              <span className="text-gray-500">Start Date:</span>
                              <span className="ml-2 text-gray-300">{formatDate(sub.start_date)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">End Date:</span>
                              <span className="ml-2 text-gray-300">{formatDate(sub.end_date)}</span>
                            </div>
                          </div>

                          {sub.telegram_group_link && status !== 'expired' && (
                            <a 
                              href={sub.telegram_group_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-golden hover:text-golden/80 text-sm"
                            >
                              <MessageCircle className="w-4 h-4" />
                              {sub.subscription_type === 'workshop' ? 'Join WhatsApp Group' : 'Join Telegram Group'}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <TrendingUp className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No active subscriptions</p>
                    <Button 
                      onClick={() => navigate('/')}
                      className="mt-4 bg-golden hover:bg-golden/90 text-black"
                    >
                      Subscribe Now
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Workshop Recordings (only for paid workshop enrollees) */}
            <WorkshopRecordings phone={user?.phone} />

            {/* Telegram Links - Only shown for ACTIVE subscriptions */}
            {hasActiveAccess && Object.keys(telegram_links || {}).length > 0 && (
              <Card className="bg-gray-900/50 border-golden/30">
                <CardHeader>
                  <CardTitle className="text-golden flex items-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    Your Telegram Groups
                  </CardTitle>
                  <CardDescription className="text-green-400 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Active Access
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(telegram_links).map(([type, link]) => (
                      <a
                        key={type}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        <span className="text-white">{packageNames[type] || type}</span>
                        <ExternalLink className="w-4 h-4 text-golden" />
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Autopay Status */}
            {autopay_subscriptions && autopay_subscriptions.length > 0 && (
              <AutopayStatus
                subscriptions={autopay_subscriptions}
                userEmail={user?.email || ''}
              />
            )}

            {/* No Access Message for Expired Users */}
            {!hasActiveAccess && (
              <Card className="bg-gray-900/50 border-red-500/30">
                <CardHeader>
                  <CardTitle className="text-red-400 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Telegram Access Expired
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6">
                    <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 mb-4">Your subscription has expired. Renew to regain access to Telegram groups.</p>
                    <Button 
                      onClick={() => navigate('/')}
                      className="bg-golden hover:bg-golden/90 text-black"
                    >
                      Renew Subscription
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Referral & Points */}
          <div className="space-y-6">
            {/* Referral Card */}
            <Card className="bg-gradient-to-br from-golden/20 to-golden/5 border-golden/30">
              <CardHeader>
                <CardTitle className="text-golden flex items-center gap-2">
                  <Gift className="w-5 h-5" />
                  Your Referral Code
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Share & earn 10 points per ₹100 spent by friends
                </CardDescription>
              </CardHeader>
              <CardContent>
                {referral?.referral_code ? (
                  <>
                    <div className="bg-gray-900 rounded-lg p-4 text-center mb-4">
                      <span className="text-2xl font-bold text-golden tracking-widest">
                        {referral.referral_code}
                      </span>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        onClick={copyReferralCode}
                        className="flex-1 bg-gray-800 hover:bg-gray-700 text-white"
                        size="sm"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        {copied ? 'Copied!' : 'Copy'}
                      </Button>
                      <Button 
                        onClick={shareReferral}
                        className="flex-1 bg-golden hover:bg-golden/90 text-black"
                        size="sm"
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        Share
                      </Button>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-700">
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold text-white">
                            {referral.successful_referrals || 0}
                          </p>
                          <p className="text-xs text-gray-400">Referrals</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-golden">
                            {referral.total_points_earned?.toFixed(0) || 0}
                          </p>
                          <p className="text-xs text-gray-400">Points Earned</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-400">Complete a purchase to get your referral code</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Points Balance */}
            <Card className="bg-gray-900/50 border-golden/30">
              <CardHeader>
                <CardTitle className="text-golden flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Points Balance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-4">
                  <p className="text-4xl font-bold text-golden">
                    {referral?.points_balance?.toFixed(0) || 0}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">Available Points</p>
                  <p className="text-xs text-gray-500 mt-2">1 Point = ₹1 discount</p>
                </div>

                {/* Points Breakdown */}
                <div className="mt-4 pt-4 border-t border-gray-700 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total Earned</span>
                    <span className="text-white">{referral?.total_points_earned?.toFixed(0) || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Redeemed</span>
                    <span className="text-white">{referral?.total_points_redeemed?.toFixed(0) || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Points Transactions */}
            {points_transactions?.length > 0 && (
              <Card className="bg-gray-900/50 border-golden/30">
                <CardHeader>
                  <CardTitle className="text-golden flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {points_transactions.slice(0, 10).map((trans, index) => (
                      <div 
                        key={trans.id || index}
                        className="flex justify-between items-center p-2 bg-gray-800/30 rounded"
                      >
                        <div>
                          <p className="text-sm text-white">{trans.description || trans.transaction_type}</p>
                          <p className="text-xs text-gray-500">{formatDate(trans.created_at)}</p>
                        </div>
                        <span className={`font-semibold ${trans.points >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {trans.points >= 0 ? '+' : ''}{trans.points?.toFixed(0)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Write a Review */}
            <ReviewForm 
              API_URL={API_URL} 
              user={user} 
              subscriptions={subscriptions}
            />
          </div>
        </div>

        {/* My Courses Section */}
        <MyCourses token={localStorage.getItem('subscriber_token')} navigate={navigate} />

        {/* Back to Home */}
        <div className="mt-8 text-center">
          <Button 
            onClick={() => navigate('/')}
            variant="outline"
            className="border-golden/50 text-golden hover:bg-golden/10"
          >
            Back to Home
          </Button>
        </div>
      </main>
    </div>
  );
};

// Review Form Component
const ReviewForm = ({ API_URL, user, subscriptions }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!title.trim() || !content.trim()) {
      setError('Please fill in all fields');
      return;
    }
    
    if (title.length < 3) {
      setError('Title must be at least 3 characters');
      return;
    }
    
    if (content.length < 10) {
      setError('Review must be at least 10 characters');
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating,
          title: title.trim(),
          content: content.trim(),
          user_name: user?.name || 'Subscriber',
          user_email: user?.email,
          user_phone: user?.phone,
          subscription_type: subscriptions?.[0]?.subscription_type
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        setError(data.detail || 'Failed to submit review');
      }
    } catch (err) {
      setError('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Card className="bg-green-900/30 border-green-500/30">
        <CardContent className="p-6 text-center">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <h4 className="text-lg font-semibold text-green-300 mb-2">Thank You!</h4>
          <p className="text-green-400 text-sm">Your review has been submitted and is pending approval.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-900/50 border-golden/30">
      <CardHeader>
        <CardTitle 
          className="text-golden flex items-center gap-2 cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <Star className="w-5 h-5" />
          Write a Review
          <span className="ml-auto text-sm text-gray-400">{isOpen ? '▲' : '▼'}</span>
        </CardTitle>
        <CardDescription className="text-gray-400">
          Share your experience with our services
        </CardDescription>
      </CardHeader>
      
      {isOpen && (
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Rating */}
            <div>
              <label className="block text-sm text-gray-300 mb-2">Rating</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className={`text-2xl transition-colors ${
                      star <= rating ? 'text-golden' : 'text-gray-600'
                    } hover:text-golden`}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm text-gray-300 mb-2">Title</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Great trading insights!"
                className="bg-gray-800 border-gray-700 text-white"
                maxLength={100}
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm text-gray-300 mb-2">Your Review</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Share your experience with our trading services..."
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:border-golden focus:outline-none min-h-[100px]"
                maxLength={1000}
              />
              <p className="text-xs text-gray-500 mt-1">{content.length}/1000</p>
            </div>

            {error && (
              <div className="text-red-400 text-sm bg-red-900/20 p-2 rounded">{error}</div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-golden hover:bg-golden/90 text-black"
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </form>
        </CardContent>
      )}
    </Card>
  );
};

// Autopay Status Component
const AutopayStatus = ({ subscriptions, userEmail }) => {
  const [cancelling, setCancelling] = useState(null);

  const cancelAutopay = async (subscriptionId) => {
    if (!window.confirm('Cancel auto-renewal? Your current billing cycle will continue until it expires.')) return;
    setCancelling(subscriptionId);
    try {
      const res = await fetch(`${API_URL}/api/autopay/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription_id: subscriptionId, cancel_at_cycle_end: true })
      });
      const data = await res.json();
      if (data.status === 'success') {
        window.location.reload();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCancelling(null);
    }
  };

  const activeAutopay = subscriptions.filter(s => s.status === 'active' || s.status === 'authenticated');
  if (activeAutopay.length === 0) return null;

  return (
    <Card className="bg-gray-900/50 border-green-500/30">
      <CardHeader>
        <CardTitle className="text-green-400 flex items-center gap-2">
          <RefreshCw className="w-5 h-5" />
          Auto-Renewal Active
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {activeAutopay.map(sub => (
            <div key={sub.subscription_id || sub.id} className="bg-gray-800/50 rounded-lg p-3 border border-green-500/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium text-sm">{sub.subscription_type || sub.plan_id || 'Monthly'}</span>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                  Active
                </Badge>
              </div>
              <div className="text-xs text-gray-400 space-y-1">
                {sub.current_end && (
                  <p>Next renewal: {new Date(sub.current_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                )}
                {sub.amount && <p>Amount: ₹{sub.amount}/month</p>}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="mt-2 border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs"
                onClick={() => cancelAutopay(sub.subscription_id)}
                disabled={cancelling === sub.subscription_id}
                data-testid={`cancel-autopay-${sub.subscription_id}`}
              >
                {cancelling === sub.subscription_id ? 'Cancelling...' : 'Cancel Auto-Renewal'}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// My Courses Component
const MyCourses = ({ token, navigate }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourses = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/courses/my-courses`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setEnrollments(data.enrollments || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, [token]);

  if (loading) return null;
  if (enrollments.length === 0) return (
    <div className="mt-6">
      <Card className="bg-gray-900/50 border-golden/30">
        <CardContent className="p-6 text-center">
          <BookOpen className="w-10 h-10 text-golden/40 mx-auto mb-3" />
          <h3 className="text-golden font-semibold mb-2">Stock Market Courses</h3>
          <p className="text-gray-400 text-sm mb-4">Browse our courses to upskill your trading knowledge</p>
          <Button onClick={() => navigate('/courses')} className="bg-golden hover:bg-golden/90 text-black" data-testid="browse-courses-btn">
            <Play className="w-4 h-4 mr-2" /> Browse Courses
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const now = new Date().toISOString();
  return (
    <div className="mt-6">
      <Card className="bg-gray-900/50 border-golden/30">
        <CardHeader>
          <CardTitle className="text-golden flex items-center gap-2">
            <BookOpen className="w-5 h-5" /> My Courses
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {enrollments.map(e => (
              <div key={e.id} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-white/5">
                <div>
                  <h4 className="text-white font-medium">{e.course_title}</h4>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    {e.level && <span>{e.level}</span>}
                    <span>{e.is_active ? 'Active' : 'Expired'}</span>
                  </div>
                </div>
                {e.is_active ? (
                  <Button size="sm" onClick={() => navigate(`/courses/${e.course_id}/learn`)} className="bg-golden text-black hover:bg-golden/90" data-testid={`learn-course-${e.course_id}`}>
                    <Play className="w-4 h-4 mr-1" /> Learn
                  </Button>
                ) : (
                  <Badge variant="outline" className="border-red-500/50 text-red-400">Expired</Badge>
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <Button variant="outline" size="sm" onClick={() => navigate('/courses')} className="border-golden/50 text-golden hover:bg-golden/10" data-testid="view-all-courses-btn">
              View All Courses
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriberDashboard;
